import React, { useState, useEffect } from 'react';
import { getPendingRequisitionsReports, exportReport } from '../../../models/reportsModel';

function PendingRequisitionsReports() {
  const [loading, setLoading] = useState(true);
  const [pendingRequisitions, setPendingRequisitions] = useState([]);
  const [filteredRequisitions, setFilteredRequisitions] = useState([]);
  const [filters, setFilters] = useState({
    priority: '',
    department: '',
    requester: '',
    ageFilter: '',
    amountRange: {
      min: '',
      max: ''
    },
    dateRange: {
      start: '',
      end: ''
    }
  });

  useEffect(() => {
    fetchPendingRequisitionsData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [pendingRequisitions, filters]);

  const fetchPendingRequisitionsData = async () => {
    try {
      setLoading(true);
      const response = await getPendingRequisitionsReports();
      if (response.status === 1 || response.status === 'success') {
        setPendingRequisitions(response.data?.pending_requisitions || response.data || []);
      }
    } catch (error) {
      console.error('Error fetching pending requisitions reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...pendingRequisitions];

    if (filters.priority) {
      filtered = filtered.filter(req => 
        req.priority?.toLowerCase().includes(filters.priority.toLowerCase())
      );
    }

    if (filters.department) {
      filtered = filtered.filter(req => 
        req.department?.toLowerCase().includes(filters.department.toLowerCase()) ||
        req.user_department?.toLowerCase().includes(filters.department.toLowerCase())
      );
    }

    if (filters.requester) {
      filtered = filtered.filter(req => 
        req.requester?.toLowerCase().includes(filters.requester.toLowerCase()) ||
        req.user_name?.toLowerCase().includes(filters.requester.toLowerCase()) ||
        req.requested_by?.toLowerCase().includes(filters.requester.toLowerCase())
      );
    }

    if (filters.ageFilter) {
      const now = new Date();
      const cutoffDays = parseInt(filters.ageFilter);
      const cutoffDate = new Date(now.getTime() - (cutoffDays * 24 * 60 * 60 * 1000));
      
      filtered = filtered.filter(req => {
        const createdDate = new Date(req.created_at || req.date_created || req.requisition_date);
        return createdDate <= cutoffDate;
      });
    }

    if (filters.dateRange.start) {
      filtered = filtered.filter(req => 
        new Date(req.created_at || req.date_created || req.requisition_date) >= new Date(filters.dateRange.start)
      );
    }

    if (filters.dateRange.end) {
      filtered = filtered.filter(req => 
        new Date(req.created_at || req.date_created || req.requisition_date) <= new Date(filters.dateRange.end)
      );
    }

    if (filters.amountRange.min) {
      filtered = filtered.filter(req => 
        parseFloat(req.total_amount || req.estimated_cost || req.amount || 0) >= parseFloat(filters.amountRange.min)
      );
    }

    if (filters.amountRange.max) {
      filtered = filtered.filter(req => 
        parseFloat(req.total_amount || req.estimated_cost || req.amount || 0) <= parseFloat(filters.amountRange.max)
      );
    }

    setFilteredRequisitions(filtered);
  };

  const handleFilterChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFilters(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFilters(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleExport = async () => {
    try {
      await exportReport('pending_requisitions', filters);
    } catch (error) {
      console.error('Error exporting report:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch (error) {
      return dateStr;
    }
  };

  const getAge = (dateStr) => {
    try {
      const createdDate = new Date(dateStr);
      const now = new Date();
      const diffTime = Math.abs(now - createdDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch (error) {
      return 0;
    }
  };

  const getAgeColor = (days) => {
    if (days <= 3) return 'bg-green-100 text-green-800';
    if (days <= 7) return 'bg-yellow-100 text-yellow-800';
    if (days <= 14) return 'bg-orange-100 text-orange-800';
    return 'bg-red-100 text-red-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'urgent': 'bg-red-100 text-red-800',
      'high': 'bg-orange-100 text-orange-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'low': 'bg-green-100 text-green-800',
      'critical': 'bg-red-100 text-red-800'
    };
    return colors[priority?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityIcon = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
      case 'critical':
        return (
          <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      case 'high':
        return (
          <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'medium':
        return (
          <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const getTotalPendingValue = () => {
    return filteredRequisitions.reduce((total, req) => 
      total + parseFloat(req.total_amount || req.estimated_cost || req.amount || 0), 0
    );
  };

  const getOverdueCount = () => {
    return filteredRequisitions.filter(req => getAge(req.created_at || req.date_created || req.requisition_date) > 7).length;
  };

  const getUrgentCount = () => {
    return filteredRequisitions.filter(req => 
      req.priority === 'urgent' || req.priority === 'critical'
    ).length;
  };

  const getAverageAge = () => {
    if (filteredRequisitions.length === 0) return 0;
    
    const totalAge = filteredRequisitions.reduce((sum, req) => 
      sum + getAge(req.created_at || req.date_created || req.requisition_date), 0
    );
    
    return Math.round(totalAge / filteredRequisitions.length);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-4">
        {/* Professional Header Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            {/* Left: Icon, Title, Description */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" 
                   style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Pending Requisitions Reports</h1>
                <p className="text-xs text-gray-500 mb-1">Monitor requisitions awaiting approval and processing</p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                    {filteredRequisitions.length} pending requisition{filteredRequisitions.length !== 1 ? 's' : ''} total
                  </span>
                </div>
              </div>
            </div>
            
            {/* Right: Controls and Actions */}
            <div className="flex flex-col gap-2 items-end min-w-0">
              <div className="flex items-center gap-2">
                <button
                  onClick={handleExport}
                  className="inline-flex items-center justify-center px-4 py-1.5 text-white text-sm font-medium rounded-md shadow-sm hover:opacity-90 transition-all duration-200"
                  style={{ 
                    backgroundColor: '#0087D4', 
                    boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)' 
                  }}
                >
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Export Report
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
              <select
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Priorities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
                <option value="critical">Critical</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
              <input
                type="text"
                placeholder="Search by department..."
                value={filters.department}
                onChange={(e) => handleFilterChange('department', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Requester</label>
              <input
                type="text"
                placeholder="Search by requester..."
                value={filters.requester}
                onChange={(e) => handleFilterChange('requester', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Age Filter</label>
              <select
                value={filters.ageFilter}
                onChange={(e) => handleFilterChange('ageFilter', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Ages</option>
                <option value="1">Older than 1 day</option>
                <option value="3">Older than 3 days</option>
                <option value="7">Older than 1 week</option>
                <option value="14">Older than 2 weeks</option>
                <option value="30">Older than 1 month</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
              <input
                type="date"
                value={filters.dateRange.start}
                onChange={(e) => handleFilterChange('dateRange.start', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
              <input
                type="date"
                value={filters.dateRange.end}
                onChange={(e) => handleFilterChange('dateRange.end', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Min Amount</label>
              <input
                type="number"
                placeholder="0.00"
                value={filters.amountRange.min}
                onChange={(e) => handleFilterChange('amountRange.min', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Max Amount</label>
              <input
                type="number"
                placeholder="0.00"
                value={filters.amountRange.max}
                onChange={(e) => handleFilterChange('amountRange.max', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Pending Requisitions Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Pending Requisitions</h3>
          </div>

          {filteredRequisitions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No pending requisitions found matching your criteria.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Requisition ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Requester
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Department
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Age (Days)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRequisitions.map((requisition, index) => {
                    const age = getAge(requisition.created_at || requisition.date_created || requisition.requisition_date);
                    return (
                      <tr key={requisition.id || index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {requisition.requisition_number || requisition.id || `REQ-${index + 1}`}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getPriorityIcon(requisition.priority)}
                            <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(requisition.priority)}`}>
                              {requisition.priority || 'Medium'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {requisition.requester || requisition.user_name || requisition.requested_by || 'Unknown'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {requisition.department || requisition.user_department || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(requisition.total_amount || requisition.estimated_cost || requisition.amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getAgeColor(age)}`}>
                            {age} days
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(requisition.created_at || requisition.date_created || requisition.requisition_date)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                          {requisition.description || requisition.purpose || requisition.details || 'No description'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PendingRequisitionsReports; 